import { CommonModule } from '@angular/common';
import { Component, computed } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonComponent } from '../../@core/components/ui/button/button.component';
import { AuthenticationService, ILogin } from '../../@core/services/authentication.service';
import { ToastService } from '../../@core/services/toast.service';
import { InvitationService } from '../../contract-management/services/invitation.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { TextCasePipe } from "../../@shared/pipes/textCase.pipe";
import { DEFAULT_PROFILE_PICTURES } from '../sign-up/constants/constants';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    ButtonComponent,
    CommonModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    ProgressBarModule,
    TooltipModule,
    TextCasePipe,
    AssetUrlPipe
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {
  profileForm!: FormGroup;
  defaultUrl: string = ''
  returnUrl: string = this.defaultUrl;
  invitationId: string = '';
  validInvite: boolean = false;
  loading: boolean = false;
  submitInProcess: boolean = false;
  tabs = [
    { label: 'Users', routerLink: 'profile', icon: 'pi pi-user' },
    { label: 'Invites', routerLink: 'people', icon: 'pi pi-users' },
  ]
  imageFile: any
  usernameValid: boolean = false

  validImageTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  maxImageSize: number = 5 * 1000 * 1000;
  userLimitExceeded: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private toastService: ToastService,
    private invitationService: InvitationService,
    private authService: AuthenticationService
  ) {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || this.defaultUrl;
  }

  ngOnInit(): void {
    this.invitationId = this.route.snapshot.queryParamMap.get('inviteId') || '';
    this.initializeProfileForm();
    if (this.invitationId) {
      this.getInviteDetails(this.invitationId);
    }
  }

  getInviteDetails(inviteId: string) {
    this.loading = true;
    this.invitationService.getInviteDetails(inviteId).subscribe((res) => {
      if (res.success) {
        this.validInvite = true;
        this.loading = false;
        // Set email from invitation data
        this.profileForm.get('email')?.setValue(res.data.invitation.email)
        this.profileForm.get('email')?.disable();
        // Set user limit exceeded flag from backend response
        this.userLimitExceeded = res.data.userLimitExceeded;
      } else {
        this.loading = false;
        this.validInvite = false;
      }
    })
  }

  avatarValidator() {
    const group = this.profileForm;
    const gender = group.get('gender')?.value;
    const currentPicture = group.get('profilePicture')?.value;

    if (!gender) {
      return true;
    }
    if (currentPicture === this.PROFILE_PICTURES().MALE || currentPicture === this.PROFILE_PICTURES().FEMALE) {
      group.get('profilePicture')?.setValue(
        gender === 'MALE' ? this.PROFILE_PICTURES().MALE : this.PROFILE_PICTURES().FEMALE
      );
    }
    return true;
  }

  passwordMatchValidator() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get('password')?.value;
      const confirmPassword = formGroup.get('confirmPassword')?.value;
      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  initializeProfileForm() {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      username: ['', Validators.required],
      gender: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
      profilePicture: [DEFAULT_PROFILE_PICTURES.MALE]
    }, { validators: this.passwordMatchValidator() });
  }

  onSubmitProfile() {
    if (this.submitInProcess) return;
    this.submitInProcess = true;
    if (!this.profileForm.valid) {
      this.toastService.error('Please fill all the required fields');
      this.submitInProcess = false;
      return
    }
    const formData = new FormData();
    formData.append('firstName', this.profileForm.value.firstName);
    formData.append('lastName', this.profileForm.value.lastName);
    formData.append('email', this.profileForm.getRawValue().email);
    formData.append('username', this.profileForm.value.username);
    formData.append('gender', this.profileForm.value.gender);
    formData.append('password', this.profileForm.value.password);
    const file = this.profileForm.get('profilePicture')?.value;
    if (file) {
      formData.append('profilePicture', this.imageFile);
    }
    formData.append('invitationId', this.invitationId);
    this.authService.userSignUpFromInvitation(formData).subscribe((res) => {
      if (res.success) {
        this.submitInProcess = false;
        const userLoggedIn = this.authService.setUserData(res.data as ILogin);
        if (userLoggedIn) {
          this.router.navigate([this.returnUrl])
        } else {
          this.toastService.error('Login Failed', 'Error!');
        }
      }
      else {
        this.submitInProcess = false;
      }
    })
  }

  isValidImageType(type: string): boolean {
    return this.validImageTypes.includes(type);
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      if (!this.isValidImageType(file.type)) {
        this.toastService.error('Please select a valid image file (JPEG, PNG, GIF)');
        input.value = '';
        return;
      }
      if (file.size > this.maxImageSize) {
        this.toastService.error('File size must be less than 5MB');
        input.value = '';
        return;
      }
      this.imageFile = file
      this.profileForm.patchValue({
        profilePicture: URL.createObjectURL(file),
      });
    }
    input.value = '';
  }

  checkUserName() {
    const username = this.profileForm.get('username')?.value;
    this.authService.checkUsername(username).subscribe(res => {
      if (res.data) {
        this.profileForm.controls['username'].setErrors({ 'alreadyExists': true });
        this.usernameValid = true
      }
      else {
        this.usernameValid = false
      }
    })
  }

  removePicture(): void {
    const gender = this.profileForm.get('gender')?.value;
    this.imageFile = null;
    this.profileForm.patchValue({
      profilePicture: gender === 'MALE' ? this.PROFILE_PICTURES().MALE : this.PROFILE_PICTURES().FEMALE
    });
  }

  PROFILE_PICTURES = computed(() => {
    return DEFAULT_PROFILE_PICTURES
  })
}
